import { Suspense, useEffect, useState } from "react";
import { lazyWithRetry } from "../../../lib/lazyWithRetry.js";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";

import { api } from "../../../lib/api.js";
import { logError } from "../../../lib/logger.js";
import { GlowCard } from "../../ui/glow-card.js";
import { CardTitle } from "../../ui/card.js";
import { StatusFilterChips } from "../../../pages/employees/StatusFilterChips.js";
import { EmployeeSummarySkeleton } from "../../../pages/employees/EmployeeSummarySkeleton.js";
import {
  employeeStatsQuery, employeeSummaryModel, type EmployeeStats,
} from "../../../pages/employees/employeeSummary.js";
import type { WidgetProps } from "../widget-registry.js";

// Recharts is ~600 kB. The Employees page already splits this module out for the
// same reason; importing it the same way here keeps it out of the entry chunk and
// shares the one chunk between the two screens.
const EmployeeSummaryCharts = lazyWithRetry(() => import("../../../pages/employees/EmployeeSummaryCharts.js"));

/**
 * The Employees page's Summary strip as a dashboard widget: the status chips, the
 * department donut, the office bars and the headcount mix, drawing the same data
 * through the same components, so the entry animations and the hover and selection
 * behaviour are the originals rather than a second implementation of them.
 *
 * Its filters are its own. Picking a department here narrows this widget's three
 * charts, exactly as it narrows the page's; it does not touch the Employees page's
 * saved filters, which belong to that screen's table.
 */
export function EmployeeReportWidget(_props: WidgetProps) {
  const [stats, setStats] = useState<EmployeeStats | null>(null);
  const [status, setStatus] = useState("");
  const [departmentId, setDepartmentId] = useState("");
  const [officeId, setOfficeId] = useState("");

  useEffect(() => {
    let cancelled = false;
    api.get<EmployeeStats>(`/employees/stats${employeeStatsQuery({ status, departmentId, officeId })}`)
      .then((res) => { if (!cancelled) setStats(res.data); })
      .catch((e: unknown) => { if (!cancelled) logError("EmployeeReportWidget.load", e); })
      .finally(() => { /* a failed load leaves the skeleton up rather than an empty card */ });
    return () => { cancelled = true; };
  }, [status, departmentId, officeId]);

  const { totalHeadcount, statusFilters, statusMix } = employeeSummaryModel(stats);

  return (
    // @container, because the three chart panels have to fold by the card's own
    // width: at Medium on a wide screen a viewport breakpoint would still claim
    // three columns inside a half-width card.
    // spotlight="off": these cards are dense with their own tinted tiles and chart
    // fills, and the interior hover pool washed over them. The edge glow stays.
    <GlowCard glow="ultra" spotlight="off" size="lg" className="@container h-full">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-x-3 gap-y-1">
        <CardTitle>Employee Report</CardTitle>
        <Link
          to="/employees"
          className="flex items-center gap-1 text-[12px] font-semibold text-brand-600 no-underline hover:underline"
        >
          All employees <ArrowUpRight className="size-3.5" />
        </Link>
      </div>

      {/* Until the first response lands there is nothing to draw, so the skeleton
          holds the exact layout and the charts then animate up from zero into it.
          Later filter changes keep the current charts on screen rather than
          flashing back to the skeleton. */}
      {stats ? (
        <>
          <StatusFilterChips
            options={statusFilters}
            value={status}
            onChange={setStatus}
            className="mb-4"
            // Clearing a donut slice or a bar means clicking that exact mark again,
            // which is easy to lose track of once two are selected -- this is the
            // one control that puts the card back to every employee.
            onReset={() => { setStatus(""); setDepartmentId(""); setOfficeId(""); }}
            resetDisabled={!status && !departmentId && !officeId}
          />
          <Suspense fallback={<EmployeeSummarySkeleton chips={false} />}>
            <EmployeeSummaryCharts
              // Two panels from ~672px of card, three from ~896px -- the donut needs
              // room for its legend, and a third of a half-width card is not it.
              gridClassName="@2xl:grid-cols-2 @4xl:grid-cols-3"
              byDept={stats.byDept ?? []}
              byOffice={stats.byOffice ?? []}
              statusMix={statusMix}
              totalHeadcount={totalHeadcount}
              filterDept={departmentId}
              filterOffice={officeId}
              filterStatus={status}
              onSelectDept={(id) => setDepartmentId(id ?? "")}
              onSelectOffice={(id) => setOfficeId(id ?? "")}
              onSelectStatus={(id) => setStatus(id ?? "")}
            />
          </Suspense>
        </>
      ) : (
        <EmployeeSummarySkeleton />
      )}
    </GlowCard>
  );
}
